// Starter sequences (X4): « Installer les séquences de départ » on the Séquences page (admins). Installed
// DISABLED, idempotent on sequences.starter_key (`<key>:<language>`), FR and EN. Steps reference the seeded
// default templates (defaultTemplates.ts) by slug + language, resolved to template ids at install time
// (sequence_steps.template_id). The admin reviews, approves the templates (drafts never send) and enables.
// SQL: public.install_starter_sequences(p_sequences) in 37_gavion_digest.sql; FakeRest twin in
// fakeEmailMethods.ts.
// delay_business_days is counted from the PREVIOUS step (from the enrolment for the first step), as the
// sequence runner does: « J+1 puis J+4 » = delays 1 and 3.
import { DEFAULT_TEMPLATES, type DefaultTemplate } from "./defaultTemplates.ts";
import type { Lang } from "./types.ts";

export type StarterStep = {
  template_slug: string;
  delay_business_days: number;
};
export type StarterSequence = {
  key: string;
  language: Lang;
  name: string;
  description: string;
  steps: readonly StarterStep[];
};

const S = (
  key: string,
  language: Lang,
  name: string,
  description: string,
  steps: StarterStep[],
): StarterSequence =>
  Object.freeze({
    key,
    language,
    name,
    description,
    steps: Object.freeze(steps),
  });

export const STARTER_SEQUENCES: readonly StarterSequence[] = Object.freeze([
  S(
    "after_voicemail",
    "fr",
    "Après message laissé",
    "Séquence de départ : J+1 courriel de suivi du message laissé, J+4 brochure. Désactivée tant que vous ne l'avez pas révisée et activée.",
    [
      { template_slug: "voicemail_followup", delay_business_days: 1 },
      { template_slug: "brochure", delay_business_days: 3 },
    ],
  ),
  S(
    "after_brochure",
    "fr",
    "Après envoi de la brochure",
    "Séquence de départ : J+3 courriel de suivi après l'envoi de la brochure. Désactivée tant que vous ne l'avez pas révisée et activée.",
    [{ template_slug: "brochure_followup", delay_business_days: 3 }],
  ),
  S(
    "after_demo",
    "fr",
    "Après démo",
    "Séquence de départ : J+1 suivi de la proposition, J+5 relance. Désactivée tant que vous ne l'avez pas révisée et activée.",
    [
      { template_slug: "proposal_followup", delay_business_days: 1 },
      { template_slug: "demo_followup_reminder", delay_business_days: 4 },
    ],
  ),
  S(
    "after_voicemail",
    "en",
    "After voicemail",
    "Starter sequence: day 1 voicemail follow-up email, day 4 brochure. Disabled until you review and enable it.",
    [
      { template_slug: "voicemail_followup", delay_business_days: 1 },
      { template_slug: "brochure", delay_business_days: 3 },
    ],
  ),
  S(
    "after_brochure",
    "en",
    "After brochure sent",
    "Starter sequence: day 3 follow-up email after the brochure. Disabled until you review and enable it.",
    [{ template_slug: "brochure_followup", delay_business_days: 3 }],
  ),
  S(
    "after_demo",
    "en",
    "After demo",
    "Starter sequence: day 1 proposal follow-up, day 5 reminder. Disabled until you review and enable it.",
    [
      { template_slug: "proposal_followup", delay_business_days: 1 },
      { template_slug: "demo_followup_reminder", delay_business_days: 4 },
    ],
  ),
]);

/**
 * The default templates the starters need (slug + language), passed to install_starter_sequences so that a
 * database seeded before a template existed (e.g. brochure_followup) gets the missing ones, as drafts.
 */
export const starterTemplates = (
  defaults: readonly DefaultTemplate[] = DEFAULT_TEMPLATES,
): DefaultTemplate[] => {
  const want = new Set(
    STARTER_SEQUENCES.flatMap((s) =>
      s.steps.map((st) => `${st.template_slug}/${s.language}`),
    ),
  );
  return defaults.filter((t) => want.has(`${t.slug}/${t.language}`));
};

export const starterKey = (s: Pick<StarterSequence, "key" | "language">) =>
  `${s.key}:${s.language}`;

/** Day offsets from the enrolment (J+n) of each step: cumulative delays. */
export const starterDayOffsets = (s: StarterSequence): number[] => {
  let total = 0;
  return s.steps.map((x) => (total += x.delay_business_days));
};
